import { type AttributionPayload, getAttribution } from '@/lib/attribution'
import type { Deal } from '@/payload-types'

type EmailFormSubmission = {
  type: 'email'
  email: string
  pageUrl: string
}

type DealFormSubmission = {
  type: 'deal'
  email: string
  pageUrl: string
  dealId?: number
}

type FormSubmission = EmailFormSubmission | DealFormSubmission

type FormSubmissionWithAttribution = FormSubmission & {
  attribution?: AttributionPayload | null
}

// Surface a server-provided message (e.g. the blocked-domain work-email
// prompt, EA-820) so the caller can show it instead of a generic failure.
async function extractErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json()
    if (typeof body?.error === 'string' && body.error) return body.error
  } catch {
    // non-JSON body — keep the generic message
  }

  return fallback
}

// EA-838: emails the deal link to an arbitrary address so a mobile visitor can
// finish a desktop-only deal on a computer. Not a claim and not a login — see
// /api/deal-desktop-link. Throws with a server-provided message on failure.
export async function sendDealDesktopLink({
  dealId,
  email,
}: {
  dealId: Deal['id']
  email: string
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/deal-desktop-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dealId, email }),
  })

  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(response, "We couldn't send your link. Please try again."),
    )
  }

  return response.json()
}

export async function submitForm(data: FormSubmission): Promise<{ success: boolean }> {
  const payload: FormSubmissionWithAttribution = {
    ...data,
    attribution: getAttribution(),
  }

  const response = await fetch('/api/form-submission', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, 'Form submission failed'))
  }

  return response.json()
}
